import { useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ClerkProvider, SignIn, SignUp, useClerk } from '@clerk/react';
import { publishableKeyFromHost } from '@clerk/react/internal';
import { shadcn } from '@clerk/themes';
import { Route, Switch, useLocation, Router as WouterRouter } from 'wouter';
import { Toaster } from '@/components/ui/toaster';
import { WelcomeUsernameDialog } from '@/components/WelcomeUsernameDialog';
import { TooltipProvider } from '@/components/ui/tooltip';
import Home from '@/pages/Home';
import Games from '@/pages/Games';
import GameDetail from '@/pages/GameDetail';
import Play from '@/pages/Play';
import Leaderboards from '@/pages/Leaderboards';
import Profile from '@/pages/Profile';
import PublicProfile from '@/pages/PublicProfile';
import Admin from '@/pages/Admin';
import NotFound from '@/pages/not-found';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
});

const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;

const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || '/'
    : path;
}

if (!clerkPubKey) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY in .env file');
}

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: 'clerk',
  options: {
    logoPlacement: 'inside' as const,
    logoLinkUrl: basePath || '/',
    logoImageUrl: `${window.location.origin}${basePath}/favicon.png`,
  },
  variables: {
    colorPrimary: 'hsl(189 100% 50%)',
    colorForeground: 'hsl(0 0% 100%)',
    colorMutedForeground: 'hsl(240 10% 65%)',
    colorDanger: 'hsl(350 100% 60%)',
    colorBackground: 'hsl(240 20% 6%)',
    colorInput: 'hsl(240 15% 15%)',
    colorInputForeground: 'hsl(0 0% 100%)',
    colorNeutral: 'hsl(240 15% 20%)',
    fontFamily: 'Outfit, sans-serif',
    borderRadius: '0.5rem',
  },
  elements: {
    rootBox: 'w-full flex justify-center',
    // Email/password-only sign-in: hide social (Google) buttons and the "or" divider
    socialButtonsRoot: '!hidden',
    socialButtons: '!hidden',
    socialButtonsBlockButton: '!hidden',
    dividerRow: '!hidden',
    divider: '!hidden',
    cardBox: 'bg-[hsl(240,15%,9%)] rounded-xl w-[440px] max-w-full overflow-hidden border border-[hsl(240,15%,16%)] shadow-[0_0_40px_rgba(0,217,255,0.1)]',
    card: '!shadow-none !border-0 !bg-transparent !rounded-none',
    footer: '!shadow-none !border-0 !bg-transparent !rounded-none border-t border-[hsl(240,15%,16%)] mt-6',
    headerTitle: 'text-foreground font-bold font-display uppercase tracking-tight text-2xl',
    headerSubtitle: 'text-muted-foreground',
    socialButtonsBlockButtonText: 'text-foreground font-medium',
    formFieldLabel: 'text-foreground font-bold uppercase text-xs tracking-wider',
    footerActionLink: 'text-primary hover:text-primary/80 font-bold',
    footerActionText: 'text-muted-foreground',
    dividerText: 'text-muted-foreground uppercase text-xs font-bold tracking-widest',
    identityPreviewEditButton: 'text-primary hover:text-primary/80',
    formFieldSuccessText: 'text-green-500',
    alertText: 'text-foreground',
    logoBox: 'justify-center mb-6',
    logoImage: 'w-16 h-16 filter drop-shadow-[0_0_10px_rgba(0,217,255,0.5)]',
    formButtonPrimary: 'bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest glow-primary rounded-none transition-all',
    formFieldInput: 'bg-[hsl(240,15%,12%)] border-[hsl(240,15%,20%)] text-foreground rounded-none focus:border-primary focus:ring-1 focus:ring-primary',
    footerAction: 'text-center py-4 bg-[hsl(240,15%,6%)]',
    dividerLine: 'bg-[hsl(240,15%,20%)]',
    alert: 'bg-[hsl(240,15%,12%)] border-[hsl(240,15%,20%)]',
    otpCodeFieldInput: 'bg-[hsl(240,15%,12%)] border-[hsl(240,15%,20%)] text-foreground focus:border-primary',
    formFieldRow: 'gap-4',
    main: 'gap-6',
  },
};

function SignInPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignIn routing="path" path={`${basePath}/sign-in`} signUpUrl={`${basePath}/sign-up`} />
    </div>
  );
}

function SignUpPage() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <SignUp routing="path" path={`${basePath}/sign-up`} signInUrl={`${basePath}/sign-in`} />
    </div>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (
        prevUserIdRef.current !== undefined &&
        prevUserIdRef.current !== userId
      ) {
        queryClient.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, queryClient]);

  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/games" component={Games} />
      <Route path="/games/:slug" component={GameDetail} />
      <Route path="/play/:slug/:mode" component={Play} />
      <Route path="/leaderboards" component={Leaderboards} />
      <Route path="/profile" component={Profile} />
      <Route path="/u/:username" component={PublicProfile} />
      <Route path="/admin" component={Admin} />
      <Route path="/sign-in/*?" component={SignInPage} />
      <Route path="/sign-up/*?" component={SignUpPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        signIn: {
          start: {
            title: 'Welcome Back',
            subtitle: 'Sign in to keep your streak alive',
          },
        },
        signUp: {
          start: {
            title: 'Join BoredFan',
            subtitle: 'Create an account to track scores and streaks',
          },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <TooltipProvider>
          <Router />
          <WelcomeUsernameDialog />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
